import React from 'react';
import { useNode } from '@craftjs/core';
import { 
  TextField, 
  FormControl, 
  FormLabel, 
  Card as MuiCard,
  CardContent,
  CardMedia,
  CardActions,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  card: {
    cursor: 'pointer',
    minHeight: '150px',
    '&:hover': {
      outline: '2px solid #2196f3',
    },
  },
  media: {
    height: 0,
    paddingTop: '56.25%', // 16:9 aspect ratio
  },
  settings: {
    padding: theme.spacing(2),
  },
}));

export const Card = ({ 
  children,
  title,
  subtitle,
  imageUrl,
  showImage,
  elevation,
  maxWidth
}) => {
  const classes = useStyles();
  const {
    connectors: { connect, drag },
  } = useNode();

  return (
    <MuiCard
      ref={(ref) => connect(drag(ref))}
      className={classes.card}
      elevation={elevation}
      style={{
        maxWidth: maxWidth ? `${maxWidth}px` : '100%',
        margin: '16px auto',
      }}
    >
      {showImage && imageUrl && (
        <CardMedia
          className={classes.media}
          image={imageUrl}
          title={title}
        />
      )}
      <CardContent>
        {title && (
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="textSecondary" component="p">
            {subtitle}
          </Typography>
        )}
        {children}
      </CardContent>
    </MuiCard>
  );
};

const CardSettings = () => {
  const classes = useStyles();
  const {
    actions: { setProp },
    title,
    subtitle,
    imageUrl,
    showImage,
    elevation,
    maxWidth,
  } = useNode((node) => ({
    title: node.data.props.title,
    subtitle: node.data.props.subtitle,
    imageUrl: node.data.props.imageUrl,
    showImage: node.data.props.showImage,
    elevation: node.data.props.elevation,
    maxWidth: node.data.props.maxWidth,
  }));

  return (
    <div className={classes.settings}>
      <FormControl fullWidth margin="normal">
        <FormLabel>Título</FormLabel>
        <TextField
          value={title}
          onChange={(e) => setProp((props) => (props.title = e.target.value))}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Subtítulo</FormLabel>
        <TextField
          value={subtitle}
          onChange={(e) => setProp((props) => (props.subtitle = e.target.value))}
          multiline
          rows={2}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={showImage}
            onChange={(e) => setProp((props) => (props.showImage = e.target.checked))}
            style={{ marginRight: 8 }}
          />
          Mostrar imagen
        </FormLabel>
      </FormControl>

      {showImage && (
        <FormControl fullWidth margin="normal">
          <FormLabel>URL de imagen</FormLabel>
          <TextField
            value={imageUrl}
            onChange={(e) => setProp((props) => (props.imageUrl = e.target.value))}
            placeholder="https://ejemplo.com/imagen.jpg"
          />
        </FormControl>
      )}

      <FormControl fullWidth margin="normal">
        <FormLabel>Elevación (sombra)</FormLabel>
        <TextField
          type="number"
          inputProps={{ min: 0, max: 24 }}
          value={elevation}
          onChange={(e) => setProp((props) => (props.elevation = parseInt(e.target.value)))}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Ancho máximo (px)</FormLabel>
        <TextField
          type="number"
          value={maxWidth}
          onChange={(e) => setProp((props) => (props.maxWidth = e.target.value))}
          placeholder="Automático"
          helperText="Deja vacío para ancho completo"
        />
      </FormControl>
    </div>
  );
};

Card.craft = {
  props: {
    title: 'Título de la tarjeta',
    subtitle: 'Esta es una descripción breve del contenido de la tarjeta.',
    imageUrl: 'https://via.placeholder.com/400x225',
    showImage: true,
    elevation: 3,
    maxWidth: 345,
  },
  related: {
    settings: CardSettings,
  },
};
